import { Link } from "react-router-dom";
import { useDraggable } from "@dnd-kit/core";
import {
  DocumentDuplicateIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Dropdown, DropdownButton, DropdownBody, DropdownItem } from "@components/atoms/Dropdown";
import { cn, formatAmount } from "@utils";
import WorkingSetIcon from "./WorkingSetIcon";

export const CARD = "flex h-full min-h-28 flex-col justify-between rounded-2xl border bg-white px-5 py-4";

const MenuRow = ({ icon: Icon, label, onClick, danger = false }) => (
  <DropdownItem onClick={onClick} closeOnComplete>
    <span className={`flex items-center gap-2 text-sm ${danger ? "text-red-600" : ""}`}>
      <Icon aria-hidden="true" className="size-4" />
      {label}
    </span>
  </DropdownItem>
);

const Figures = ({ set }) =>
  set.isError ? (
    <span className="text-sm text-zinc-400">Couldn't load</span>
  ) : (
    <span className="flex min-w-0 flex-col gap-1">
      <span className="text-[26px] font-semibold leading-none tabular-nums text-zinc-900">
        {set.isLoading ? <span className="text-zinc-300">—</span> : set.count}
      </span>
      <span className="truncate text-sm tabular-nums text-zinc-500">
        {set.isLoading || set.overdue == null ? "\u00a0" : formatAmount(set.overdue)}
      </span>
    </span>
  );

const Heading = ({ set, children }) => (
  <span className="flex items-center justify-between gap-2">
    <span className="flex min-w-0 items-center gap-2 text-sm font-medium text-zinc-800">
      <WorkingSetIcon icon={set.icon} colour={set.colour} className="size-6 rounded-md" iconClassName="size-3.5" />
      <span className="truncate" title={set.name}>{set.name}</span>
    </span>
    {children}
  </span>
);

// What follows the pointer while a card is being dragged.
export const WorkingSetCardOverlay = ({ set }) => (
  <div className={cn(CARD, "cursor-grabbing gap-2 border-zinc-300 shadow-xl ring-1 ring-black/5 rotate-[1.5deg]")}>
    <Heading set={set} />
    <Figures set={set} />
  </div>
);

// Enter/Space on the link or menu would otherwise bubble to the card and pick it up, and a press
// on the menu shouldn't start a drag.
const stopDragKeys = (e) => {
  if (e.key === "Enter" || e.key === " ") e.stopPropagation();
};
const stopDrag = (e) => e.stopPropagation();

// The whole card opens the set in Trade Accounts via a stretched link and can be dragged to any
// slot on the board. The menu sits above the link rather than inside it, so its buttons aren't
// nested in an anchor; a click that ends a drag is swallowed so dropping never navigates.
const WorkingSetCard = ({ set, onEdit, onDuplicate, onRemove, wasJustDragged }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: set.id, data: { set } });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      role={undefined}
      aria-roledescription="Draggable working set. Press space to pick up, arrows to move, space to drop."
      className={cn(
        CARD,
        "group relative cursor-grab gap-2 border-zinc-200 transition-[border-color,box-shadow,opacity] hover:border-zinc-300 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500",
        isDragging && "opacity-30",
      )}
    >
      <Link
        to={`/accounts?set=${set.id}`}
        aria-label={`Open ${set.name} in Trade accounts`}
        draggable={false}
        onClickCapture={(e) => {
          if (wasJustDragged()) e.preventDefault();
        }}
        onKeyDown={stopDragKeys}
        className="absolute inset-0 rounded-2xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
      />
      <Heading set={set}>
        <div className="relative z-10 -my-1 -mr-2" onKeyDown={stopDragKeys} onPointerDown={stopDrag}>
          <Dropdown>
            <DropdownButton className="flex size-7 cursor-pointer items-center justify-center rounded-md text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 focus-visible:opacity-100 group-hover:opacity-100 data-[open]:opacity-100">
              <span className="sr-only">{`${set.name} options`}</span>
              <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
            </DropdownButton>
            <DropdownBody position="left" className="w-48">
              <MenuRow icon={PencilSquareIcon} label="Edit" onClick={() => onEdit(set)} />
              <MenuRow icon={DocumentDuplicateIcon} label="Duplicate" onClick={() => onDuplicate(set)} />
              <MenuRow icon={TrashIcon} label="Remove" danger onClick={() => onRemove(set)} />
            </DropdownBody>
          </Dropdown>
        </div>
      </Heading>
      <Figures set={set} />
    </div>
  );
};

export default WorkingSetCard;
